import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { pick } from "lodash";
import { useCallback } from "react";
import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import { UserDataResponse } from "../../../services/@types";
import api from "../../../services/api";
import { advancedSearchKeys } from "../../samples/queryKeys";
import { GridConfig, SamplesLayout } from "../@types";

const INITIAL_SEARCH_RESULTS_KEYS = [
  "initial_search_results",
  "favorite_query_id"
];

const GRID_CONFIG_KEYS = [
  "submission_user",
  "submission_time",
  "last_seen",
  "yara_rule",
  "show_latest_match_time",
  "show_last_updated",
  "show_new_matches",
  "show_yara_ruleset_source"
];

const SEARCH_PAGE_SETTINGS_KEYS = [
  "query_builder",
  "automatically_expand_search_period",
  "automatically_switch_to_cloud",
  "page_size"
];

const DEFAULT_SEARCH_PAGE_SETTINGS = {
  automatically_expand_search_period: true,
  automatically_switch_to_cloud: true
};

type SearchColumns =
  | "submission_user"
  | "submission_time"
  | "last_seen"
  | "yara_rule"
  | "show_latest_match_time"
  | "show_last_updated"
  | "show_new_matches";

type InitialSearchConfig = "initial_search_results" | "favorite_query_id";

type PageSettingsConfig =
  | "query_builder"
  | "automatically_expand_search_period"
  | "automatically_switch_to_cloud"
  | "page_size";

type PatchRequest = {
  exact_date: boolean;
  search_columns: Partial<Record<SearchColumns, boolean>>;
  initial_search_config: Partial<Record<InitialSearchConfig, string>>;
  page_settings: {
    search: Partial<Record<PageSettingsConfig, boolean | number>>;
  };
  samples_layout: SamplesLayout;
};

const flattenUserData = (data?: UserDataResponse | null): GridConfig => {
  if (!data) return {};
  return {
    ...data.search_columns,
    exact_date: data.exact_date,
    ...data.initial_search_config,
    ...(data.page_settings?.search ?? DEFAULT_SEARCH_PAGE_SETTINGS),
    samples_layout: data.samples_layout
  };
};

const UPDATE_USER_DATA_KEY = ["updateUserData"];

/**
 * Hook used for Grid Config retrieval
 * It exposes postConfig function used to update
 * exact_date, submission_user and submission_time column state
 */
export const useGridConfig = () => {
  const { data: userData, refetch } = useQuery<
    AxiosResponse<UserDataResponse>,
    unknown,
    GridConfig
  >({
    queryKey: advancedSearchKeys.fetchUserData,
    queryFn: () => api.getUserData(),
    select: ({ data }) => flattenUserData(data),
    refetchOnMount: false
  });

  const { mutate: updateUserData } = useMutation<
    AxiosResponse<UserDataResponse>,
    unknown,
    Partial<PatchRequest>
  >({
    mutationKey: UPDATE_USER_DATA_KEY,
    mutationFn: (request: Partial<PatchRequest>) => api.patchUserData(request),
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      show({
        level: NotificationsLevel.ERROR,
        message: "Error happened while saving user configuration."
      });
    }
  });

  const postConfig = useCallback(
    (gridConfig: GridConfig) => {
      if (GRID_CONFIG_KEYS.some(key => key in gridConfig)) {
        const searchColumns = pick(gridConfig, GRID_CONFIG_KEYS);
        updateUserData({
          search_columns: searchColumns
        });
      }

      if (INITIAL_SEARCH_RESULTS_KEYS.some(key => key in gridConfig)) {
        const initialSearchResults = pick(
          gridConfig,
          INITIAL_SEARCH_RESULTS_KEYS
        );
        updateUserData({
          initial_search_config: initialSearchResults
        });
      }

      if (SEARCH_PAGE_SETTINGS_KEYS.some(key => key in gridConfig)) {
        const searchPageSettings = pick(gridConfig, SEARCH_PAGE_SETTINGS_KEYS);
        updateUserData({
          page_settings: {
            search: searchPageSettings
          }
        });
      }

      if (gridConfig.samples_layout) {
        updateUserData({
          samples_layout: gridConfig.samples_layout
        });
      }

      if ("exact_date" in gridConfig) {
        updateUserData({
          exact_date: gridConfig.exact_date
        });
      }
    },
    [updateUserData]
  );

  return {
    postConfig,
    gridConfig: userData ?? {}
  };
};
